/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\CrmConfigurationConfig.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Integrations Config module.
----------------------------------------------------------------*/

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { ModuleFormHeader } from '../../../components/layouts';
import { Settings, Bot } from 'lucide-react';
import FixedActionBar from '../../../components/ui/FixedActionBar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { api } from '../../../lib/api';
import { useSaveWithAction } from '../../../hooks/useSaveWithAction';
import { useNotifications } from '../../../contexts/NotificationContext';
import { AgentAiTab } from '../components/AgentAiTab';
import { CrmConfigurationTab } from '../components/CrmConfigurationTab';

export const CrmConfigurationConfig = () => {
    // Support both :id (legacy) and :provider/:connectionId (new)
    const { id, provider, connectionId: urlConnectionId } = useParams<{ id?: string; provider?: string; connectionId?: string }>();
    const providerId = provider || id || '';
    const isNewConnection = urlConnectionId === 'new';
    const editConnectionId = urlConnectionId && urlConnectionId !== 'new' ? urlConnectionId : null;

    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    // CRM-specific field configuration
    const crmFieldConfig: Record<string, {
        authTypes: string[];
        requiresServerUrl: boolean;
        requiresTenantId: boolean;
        requiresDataCenter: boolean;
        requiresDatabaseName: boolean;
        requiresTimezone?: boolean;  // For Calendly timezone selection
        serverUrlLabel?: string;
        serverUrlPlaceholder?: string;
        serverUrlHelp?: string;
    }> = {
        hubspot: {
            authTypes: ['oauth2'],
            requiresServerUrl: false,
            requiresTenantId: false,
            requiresDataCenter: false,
            requiresDatabaseName: false
        },
        salesforce: {
            authTypes: ['oauth2'],
            requiresServerUrl: true,
            requiresTenantId: false,
            requiresDataCenter: false,
            requiresDatabaseName: false,
            serverUrlLabel: 'Instance URL (Optional)',
            serverUrlPlaceholder: 'https://yourcompany.my.salesforce.com',
            serverUrlHelp: 'Leave empty. Retrieved automatically after OAuth. Only set for sandbox environments.'
        },
        zoho: {
            authTypes: ['oauth2'],
            requiresServerUrl: false,
            requiresTenantId: false,
            requiresDataCenter: true,
            requiresDatabaseName: false
        },
        pipedrive: {
            authTypes: ['oauth2', 'api_key'],
            requiresServerUrl: false,
            requiresTenantId: false,
            requiresDataCenter: false,
            requiresDatabaseName: false
        },
        dynamics: {
            authTypes: ['oauth2'],
            requiresServerUrl: true,
            requiresTenantId: true,
            requiresDataCenter: false,
            requiresDatabaseName: false,
            serverUrlLabel: 'Environment URL',
            serverUrlPlaceholder: 'https://yourorg.crm.dynamics.com',
            serverUrlHelp: 'Found in Dynamics 365 > Settings > Developer Resources'
        },
        odoo: {
            authTypes: ['oauth2', 'api_key'],
            requiresServerUrl: true,
            requiresTenantId: false,
            requiresDataCenter: false,
            requiresDatabaseName: true,
            serverUrlLabel: 'Odoo Server URL',
            serverUrlPlaceholder: 'https://yourcompany.odoo.com',
            serverUrlHelp: 'Your Odoo instance URL'
        },
        calendly: {
            authTypes: ['oauth2', 'api_key'],
            requiresServerUrl: false,
            requiresTenantId: false,
            requiresDataCenter: false,
            requiresDatabaseName: false,
            requiresTimezone: true  // Calendly needs timezone for AI scheduling
        }
    };

    // Zoho Data Centers
    const zohoDataCenters = [
        { value: 'com', label: 'United States (.com)' },
        { value: 'eu', label: 'Europe (.eu)' },
        { value: 'in', label: 'India (.in)' },
        { value: 'com.au', label: 'Australia (.com.au)' },
        { value: 'jp', label: 'Japan (.jp)' },
        { value: 'com.cn', label: 'China (.com.cn)' },
        { value: 'ca', label: 'Canada (.ca)' },
        { value: 'sa', label: 'Saudi Arabia (.sa)' },
        { value: 'uk', label: 'United Kingdom (.uk)' }
    ];

    const currentCrmConfig = crmFieldConfig[providerId] || crmFieldConfig.hubspot;

    // Initial configuration state (default values)
    const initialConfig = {
        name: 'Default',
        enabled: false,
        authType: 'oauth2',
        clientId: '',
        clientSecret: '',
        serverUrl: '',
        apiKey: '',
        pathToken: '',
        syncContacts: true,
        syncCalls: true,
        // CRM-specific fields
        tenantId: '',      // For Dynamics 365
        dataCenter: 'com', // For Zoho
        databaseName: '',  // For Odoo
        timezone: 'America/Guatemala'  // For Calendly
    };

    // Form state
    const [config, setConfig] = React.useState(initialConfig);
    const [originalConfig, setOriginalConfig] = React.useState(initialConfig);
    const [isSaving, setIsSaving] = React.useState(false);

    // Connection state - stores the existing connection from database
    const [connectionId, setConnectionId] = React.useState<string | null>(null);
    const [isConnected, setIsConnected] = React.useState(false);

    // AI Settings state
    const [aiSettings, setAiSettings] = React.useState({
        ai_enabled: false,
        ai_allowed_actions: [] as string[],
        ai_policies: {
            require_confirmation_for_writes: true,
            max_actions_per_conversation: 10,
            timeout_ms: 5000,
            retry_attempts: 2,
            redact_logs: true,
            fallback_behavior: 'continue' as 'continue' | 'escalate' | 'fail',
            escalation_destination_type: '',
            escalation_destination: ''
        }
    });

    // Tools Schema state (OpenAI tool definitions JSON)
    const [toolsSchema, setToolsSchema] = React.useState<string>('');

    // Fetch existing connection on mount
    React.useEffect(() => {
        const fetchConnection = async () => {
            try {
                // If editing a specific connection, load it by ID
                if (editConnectionId) {
                    const response = await api.get(`/api/integrations/crm/connections/${editConnectionId}`);
                    const existingConnection = response.data;
                    if (existingConnection) {
                        setConnectionId(existingConnection.id);
                        setIsConnected(existingConnection.status === 'CONNECTED');
                        // Load all config from settings
                        const settings = existingConnection.settings || {};
                        setConfig(prev => ({
                            ...prev,
                            name: existingConnection.name || 'Default',
                            enabled: settings.enabled ?? true,
                            authType: existingConnection.authType?.toLowerCase().includes('oauth') ? 'oauth2' : 'api_key',
                            clientId: settings.clientId || '',
                            clientSecret: settings.clientSecret || '',
                            serverUrl: settings.serverUrl || '',
                            apiKey: settings.apiKey || '',
                            tenantId: settings.tenantId || '',
                            dataCenter: settings.dataCenter || 'com',
                            databaseName: settings.databaseName || '',
                            timezone: settings.timezone || 'America/Guatemala',
                            syncContacts: settings.syncContacts ?? true,
                            syncCalls: settings.syncCalls ?? true,
                        }));
                        if (settings.ai_enabled !== undefined) {
                            setAiSettings({
                                ai_enabled: settings.ai_enabled || false,
                                ai_allowed_actions: settings.ai_allowed_actions || [],
                                ai_policies: { ...aiSettings.ai_policies, ...settings.ai_policies }
                            });
                        }
                        // Load tools schema
                        if (existingConnection.toolsSchema) {
                            setToolsSchema(
                                typeof existingConnection.toolsSchema === 'string'
                                    ? existingConnection.toolsSchema
                                    : JSON.stringify(existingConnection.toolsSchema, null, 2)
                            );
                        }
                    }
                } else if (!isNewConnection && providerId) {
                    // Legacy: find first connection for provider
                    const response = await api.get('/api/integrations/crm/connections');
                    const connections = response.data || [];
                    const existingConnection = connections.find(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (c: any) => c.provider?.toLowerCase() === providerId.toLowerCase()
                    );
                    if (existingConnection) {
                        setConnectionId(existingConnection.id);
                        setIsConnected(existingConnection.status === 'CONNECTED');
                        // Load all config from settings
                        const settings = existingConnection.settings || {};
                        setConfig(prev => ({
                            ...prev,
                            name: existingConnection.name || 'Default',
                            enabled: settings.enabled ?? true,
                            authType: existingConnection.authType?.toLowerCase().includes('oauth') ? 'oauth2' : 'api_key',
                            clientId: settings.clientId || '',
                            clientSecret: settings.clientSecret || '',
                            serverUrl: settings.serverUrl || '',
                            apiKey: settings.apiKey || '',
                            tenantId: settings.tenantId || '',
                            dataCenter: settings.dataCenter || 'com',
                            databaseName: settings.databaseName || '',
                            timezone: settings.timezone || 'America/Guatemala',
                            syncContacts: settings.syncContacts ?? true,
                            syncCalls: settings.syncCalls ?? true,
                        }));
                        if (settings.ai_enabled !== undefined) {
                            setAiSettings({
                                ai_enabled: settings.ai_enabled || false,
                                ai_allowed_actions: settings.ai_allowed_actions || [],
                                ai_policies: { ...aiSettings.ai_policies, ...settings.ai_policies }
                            });
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to fetch connection:', error);
            }
        };
        if (providerId) {
            fetchConnection();
        }
    }, [providerId, editConnectionId, isNewConnection]);

    const isOauth = config.authType === 'oauth2';
    const needsServerUrl = currentCrmConfig.requiresServerUrl;

    // Auto-generate security token if missing
    React.useEffect(() => {
        if (!config.pathToken) {
            // Check if crypto.randomUUID is available, otherwise fallback or just leave empty?
            // Assuming modern browser environment for this feature
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                setConfig(prev => ({ ...prev, pathToken: crypto.randomUUID() }));
            } else {
                // Fallback for older environments if needed, or simple random string
                const randomToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                setConfig(prev => ({ ...prev, pathToken: randomToken }));
            }
        }
    }, [config.pathToken]);

    // Construct redirect URI based on current origin (read-only) with security token
    const redirectUri = `${window.location.origin}/crm/callback/${providerId}/${config.pathToken}`;

    // Handle Cancel - Restore original state
    const handleCancel = () => {
        setConfig({ ...originalConfig });
        showSuccess(t('common.changesDiscarded', 'Changes discarded'));
    };

    // Real save function - Updates AI settings for existing connection
    const handleSaveInternal = async () => {
        if (!connectionId) {
            // No connection exists yet - must connect OAuth first
            console.warn('No connection exists yet, cannot save AI settings');
            showError(t('integrations.mustConnectFirst', 'You must connect to the provider first before saving AI settings. Use the "Connect with Provider" button.'));
            setOriginalConfig({ ...config });
            return { id, ...config };
        }

        setIsSaving(true);
        try {
            // Parse toolsSchema JSON if provided
            let parsedToolsSchema = null;
            if (toolsSchema && toolsSchema.trim()) {
                try {
                    parsedToolsSchema = JSON.parse(toolsSchema);
                } catch (e) {
                    showError(t('integrations.agentAi.invalidToolsJson', 'Invalid JSON in Tools Schema. Please fix the syntax errors.'));
                    setIsSaving(false);
                    return;
                }
            }

            // Call API to update AI settings for the existing connection
            await api.put(`/api/integrations/crm/connections/${connectionId}/settings`, {
                ...aiSettings,
                toolsSchema: parsedToolsSchema,
                // Include general settings that can be changed after connection
                timezone: config.timezone
            });
            setOriginalConfig({ ...config });
            showSuccess(t('common.savedSuccessfully', 'Settings saved successfully'));
            return { id, ...config };
        } catch (error: any) {
            console.error('Failed to save settings:', error);
            showError(t('common.saveFailed', { error: error?.response?.data?.message || error?.message || 'Unknown error' }));
            throw error;
        } finally {
            setIsSaving(false);
        }
    };

    const handleConnect = async () => {
        if (!config.clientId || !config.clientSecret) {
            showSuccess(t('crm.missingCredentials', 'Please entering credentials')); // Should be error, using success for notification visibility check? No, use error if available? NotificationContext usually has showError.
            return;
        }

        try {
            setIsSaving(true);
            // Save state to localStorage for callback retrieval
            localStorage.setItem(`crm_pending_${providerId}`, JSON.stringify(config));

            // Get Auth URL - include CRM-specific params
            const params: Record<string, string> = {
                provider: providerId,
                clientId: config.clientId,
                clientSecret: config.clientSecret,
                redirectUri: redirectUri,
                state: config.pathToken,
            };

            // Add CRM-specific parameters
            if (config.serverUrl) params.serverUrl = config.serverUrl;
            if (config.tenantId) params.tenantId = config.tenantId;
            if (config.dataCenter) params.dataCenter = config.dataCenter;
            if (config.databaseName) params.databaseName = config.databaseName;

            const response = await api.get('/api/integrations/crm/auth-url', { params });

            // Redirect
            window.location.href = response.data.url;

        } catch (error) {
            console.error(error);
            setIsSaving(false);
            // Fallback error
            showError(t('integrations.connectFailed', 'Failed to initiate connection'));
        }
    };

    const handleConnectWithToken = async () => {
        if (!config.apiKey) {
            showError(t('crm.missingApiKey', 'Please enter your API Key / Personal Access Token'));
            return;
        }

        try {
            setIsSaving(true);

            // Call backend to save connection with token
            const response = await api.post('/api/integrations/crm/connect-token', {
                provider: providerId,
                accessToken: config.apiKey,
                name: config.name
            });

            // Update local state
            setConnectionId(response.data.id);
            setIsConnected(true);
            setOriginalConfig({ ...config });
            showSuccess(t('crm.connectionSuccess', 'Successfully connected!'));
        } catch (error: any) {
            console.error('Token connection error:', error);
            showError(t('crm.connectionFailed', { error: error?.response?.data?.message || error?.message || 'Unknown error' }));
        } finally {
            setIsSaving(false);
        }
    };


    // Use reusable hook for save actions
    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/settings/integrations',
        newPath: '/settings/integrations',
        mode: 'edit',
        getEditPath: () => `/settings/integrations/${providerId}`,
        getFormData: () => ({ config }),
        storageKey: 'integration_config:saveAction',
    });

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Settings className="h-5 w-5 text-primary" />}
                title={isNewConnection
                    ? `${t('common.new')} ${t(`integrations.providers.${providerId}.name`) || providerId}`
                    : `${t('common.edit')}: ${config.name} (${t(`integrations.providers.${providerId}.name`) || providerId})`
                }
                backButtonText={t('common.list', 'List')}
                onBack={() => navigate(-1)}
                actions={[]}
            />

            <div className="mt-8">
                <Tabs defaultValue="configuration" className="w-full">
                    <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1 overflow-x-auto flex-nowrap">
                        <TabsTrigger value="configuration">
                            {t('integrations.configuration.title')}
                        </TabsTrigger>
                        <TabsTrigger value="agentAi">
                            {t('integrations.agentAi.title')}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="configuration" className="space-y-6">
                        <CrmConfigurationTab
                            providerId={providerId}
                            config={config}
                            onConfigChange={setConfig}
                            currentCrmConfig={currentCrmConfig}
                            zohoDataCenters={zohoDataCenters}
                            redirectUri={redirectUri}
                            isConnected={isConnected}
                            isSaving={isSaving}
                            onConnect={handleConnect}
                            onConnectWithToken={handleConnectWithToken}
                            t={t}
                        />
                    </TabsContent>

                    <TabsContent value="agentAi">
                        <AgentAiTab
                            provider={providerId}
                            settings={aiSettings}
                            onSettingsChange={(settings) => setAiSettings({
                                ...settings,
                                ai_policies: {
                                    ...settings.ai_policies,
                                    escalation_destination_type: settings.ai_policies.escalation_destination_type || '',
                                    escalation_destination: settings.ai_policies.escalation_destination || ''
                                }
                            })}
                            toolsSchema={toolsSchema}
                            onToolsSchemaChange={setToolsSchema}
                        />
                    </TabsContent>
                </Tabs>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                cancelText={t('common.cancel')}
                useSaveDropdown={true}
                saveDropdownStorageKey="crm_config:saveAction"
                isEditMode={true}
                isSaving={isSaving}
            />
        </div >
    );
};
